import { useEffect, useRef } from 'react'
import { BarcodeElement } from '@/types'
import { useSettingsStore } from '@/stores/settingsStore'
import { useLabelStore } from '@/stores/labelStore'
import bwipjs from 'bwip-js'

interface BarcodeElementPreviewProps {
  element: BarcodeElement
  zoom: number
  mmToPx: number
}

const barcodeTypeMap: Record<string, string> = {
  'barcode-code128': 'code128',
  'barcode-code39': 'code39',
  'barcode-ean13': 'ean13',
  'barcode-ean8': 'ean8',
  'barcode-upca': 'upca',
  'barcode-upce': 'upce',
  'barcode-itf': 'interleaved2of5',
}

export function BarcodeElementPreview({ element, zoom, mmToPx }: BarcodeElementPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { variableData } = useSettingsStore()
  const { label } = useLabelStore()

  // Get display content
  let content = element.content
  if (element.isVariable && element.variableName) {
    content = variableData[element.variableName] || element.content
  }

  useEffect(() => {
    if (!canvasRef.current || !content) return

    const bcType = barcodeTypeMap[element.type] || 'code128'

    // Calculate dimensions to match ZPL output
    // In ZPL:
    // - moduleWidth is in dots (narrow bar width)
    // - height is in mm, converted to dots for the ^BC command
    const dotsPerMm = label.dpi / 25.4

    // For bwip-js:
    // - scale: pixels per module (how wide is a narrow bar in pixels)
    // - height: barcode height in millimeters
    //
    // We need to render at screen pixels. mmToPx converts mm to screen pixels.
    // moduleWidth is in dots, so we convert dots to mm, then mm to pixels.
    const moduleWidthMm = element.moduleWidth / dotsPerMm
    const scale = moduleWidthMm * mmToPx * zoom

    // bwip-js height is in mm - pass the element height directly
    const heightMm = element.height

    try {
      bwipjs.toCanvas(canvasRef.current, {
        bcid: bcType,
        text: content,
        scale: scale,
        height: heightMm,
        includetext: element.showText,
        textxalign: 'center',
      })
    } catch (e) {
      // Invalid barcode data - show placeholder
      const ctx = canvasRef.current.getContext('2d', { willReadFrequently: true })
      if (ctx) {
        const heightPx = element.height * mmToPx * zoom
        canvasRef.current.width = 100 * zoom
        canvasRef.current.height = heightPx
        ctx.fillStyle = '#f0f0f0'
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height)
        ctx.fillStyle = '#666'
        ctx.font = `${12 * zoom}px Arial`
        ctx.textAlign = 'center'
        ctx.fillText('Invalid barcode', canvasRef.current.width / 2, canvasRef.current.height / 2)
      }
    }
  }, [element, content, zoom, mmToPx, label.dpi])

  return <canvas ref={canvasRef} />
}
